/**
 * bh-collapse.js — smooth height animation for anything that shows, hides, opens or closes,
 * so the content below glides instead of jumping. Markup-driven: add `data-bh-collapse`.
 *
 *   <details data-bh-collapse>          open/close animates (summary click is intercepted)
 *   <div data-bh-collapse …>            any show/hide animates, however the code does it:
 *                                        .bh-is-hidden, [hidden], style.display — the calling
 *                                        code doesn't change. We watch the result, not the cause.
 *
 * Show:  height 0 → natural, fading in.
 * Hide:  the element is already gone when we hear about it, so a stripped, inert copy takes its
 *        place and collapses to 0. That is what stops the page below from snapping up.
 *
 * Height is animated with the Web Animations API, which never writes the style attribute, so
 * it can't retrigger our own observer. Reduced motion: nothing animates.
 * Not used for .bh-cake-info rows — bh-cake-info.js owns those (sticky header logic).
 */
(function () {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
  const DUR = 260;

  const shown = (el) => el.getClientRects().length > 0;
  const box = (el) => {
    const s = getComputedStyle(el);
    return { h: el.offsetHeight, mt: s.marginTop, mb: s.marginBottom };
  };

  function grow(el) {
    const b = box(el);
    el.animate(
      [
        { height: '0px', marginTop: '0px', marginBottom: '0px', opacity: 0, overflow: 'hidden' },
        { height: b.h + 'px', marginTop: b.mt, marginBottom: b.mb, opacity: 1, overflow: 'hidden' },
      ],
      { duration: DUR, easing: EASE }
    );
  }

  function shrink(el, b) {
    if (!b || !b.h) return;
    const ghost = el.cloneNode(true);
    // An inert copy: no ids (getElementById must keep finding the real one), no form fields
    // that could submit, invisible to assistive tech, and not observed.
    ghost.removeAttribute('id');
    ghost.removeAttribute('data-bh-collapse');
    ghost.querySelectorAll('[id]').forEach((n) => n.removeAttribute('id'));
    ghost.querySelectorAll('[name]').forEach((n) => n.removeAttribute('name'));
    ghost.hidden = false;
    ghost.classList.remove('bh-is-hidden');
    ghost.style.display = getComputedStyle(el).display === 'none' ? (b.display || 'block') : '';
    ghost.setAttribute('aria-hidden', 'true');
    ghost.inert = true;
    el.after(ghost);
    const a = ghost.animate(
      [
        { height: b.h + 'px', marginTop: b.mt, marginBottom: b.mb, opacity: 1, overflow: 'hidden' },
        { height: '0px', marginTop: '0px', marginBottom: '0px', opacity: 0, overflow: 'hidden' },
      ],
      { duration: DUR, easing: EASE }
    );
    a.onfinish = a.oncancel = () => ghost.remove();
  }

  function watch(el) {
    if (el.__bhCollapse) return;
    el.__bhCollapse = true;
    let visible = shown(el);
    // Remember the last rendered size + display: once hidden there is nothing left to measure.
    let last = visible ? { ...box(el), display: getComputedStyle(el).display } : null;
    const ro = new ResizeObserver(() => {
      if (shown(el)) last = { ...box(el), display: getComputedStyle(el).display };
    });
    ro.observe(el);
    new MutationObserver(() => {
      const now = shown(el);
      if (now === visible) return;
      visible = now;
      // Ignore changes while an ancestor is hidden (nothing on screen moves) and reduced motion.
      if (reduce.matches || (el.parentElement && !shown(el.parentElement))) return;
      if (now) grow(el);
      else shrink(el, last);
    }).observe(el, { attributes: true, attributeFilter: ['class', 'hidden', 'style'] });
  }

  function details(d) {
    if (d.__bhCollapse) return;
    d.__bhCollapse = true;
    const summary = d.querySelector(':scope > summary');
    if (!summary) return;
    summary.addEventListener('click', (e) => {
      if (reduce.matches) return;
      e.preventDefault();
      if (d.__anim) return;
      const from = d.offsetHeight;
      if (!d.open) d.open = true;
      else d.__closing = true;
      // Closed height = the summary plus the details' own vertical padding and border.
      const s = getComputedStyle(d);
      const chrome = ['paddingTop', 'paddingBottom', 'borderTopWidth', 'borderBottomWidth']
        .reduce((n, k) => n + parseFloat(s[k]), 0);
      const to = d.__closing ? summary.offsetHeight + chrome : d.offsetHeight;
      d.__anim = d.animate(
        [
          { height: from + 'px', overflow: 'hidden' },
          { height: to + 'px', overflow: 'hidden' },
        ],
        { duration: DUR, easing: EASE }
      );
      d.__anim.onfinish = d.__anim.oncancel = () => {
        if (d.__closing) d.open = false;
        d.__closing = false;
        d.__anim = null;
      };
    });
  }

  function init(root) {
    root.querySelectorAll('details[data-bh-collapse]').forEach(details);
    root.querySelectorAll('[data-bh-collapse]:not(details)').forEach(watch);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document));
  else init(document);
  // Sections re-rendered by the theme editor / section rendering API
  document.addEventListener('shopify:section:load', (e) => init(e.target));
  // Marked elements built later by JS (e.g. the configurator's add-on text fields).
  new MutationObserver((muts) => muts.forEach((m) => m.addedNodes.forEach((n) => {
    if (n.nodeType !== 1 || n.inert) return;
    if (n.matches('[data-bh-collapse]')) (n.tagName === 'DETAILS' ? details : watch)(n);
    if (n.querySelector('[data-bh-collapse]')) init(n);
  }))).observe(document.documentElement, { childList: true, subtree: true });
})();
